import firebase from "firebase/app";

import { Card } from "semantic-ui-react";

import React from "react";
import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import useCollectionData from "astrid-firestore/src/hooks/useCollectionData";
import useCollectionDataByIds from "astrid-firestore/src/hooks/useCollectionDataByIds";
import uniqueArray from "astrid-helpers/src/uniqueArray";

import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";
import useSortBy from "../../../ui/hooks/useSortBy";

import HomeLayout from "../../layouts/HomeLayout/HomeLayout";

import DiscussionFeedListItem from "./components/DiscussionsFeedListItem";

export default function DiscussionsFeedWidget({ organization, language }) {
	const { t } = useTranslation();

	const twoDaysAgo = new Date();
	twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
	twoDaysAgo.setHours(23, 59, 59, 999);

	const channelsQuery = db
		.collection("discussionChannels")
		.where("deleted", "==", false)
		.where("needsAttention", "==", true)
		.where("lastComment.created", "<", twoDaysAgo)
		.where("producerId", "==", organization.id)
		.orderBy("lastComment.created", "desc")
		.limit(30);

	const [discussionChannels, loading, error] = useCollectionData(
		language ? channelsQuery.where("subject.language", "==", language) : channelsQuery,
	);

	const [needsAttentionComments, commentsLoading, commentsError] = useCollectionData(
		db
			.collection("comments")
			.where("deleted", "==", false)
			.where("needsAttention", "==", true)
			.where("producerId", "==", organization.id),
	);

	const sortedNeedsAttentionComments = useSortBy(needsAttentionComments, "created.seconds");

	const [needsAttentionChannels, channelsLoading, channelsError] = useCollectionDataByIds(
		firebase,
		db.collection("discussionChannels"),
		uniqueArray(needsAttentionComments?.map((comment) => comment.channels?.[0])),
	);

	const needsAttention = sortedNeedsAttentionComments
		.map((comment) => ({
			comment,
			channel: needsAttentionChannels?.find((channel) => channel.id === comment.channels?.[0]),
		}))
		.filter(({ channel }) => channel && (!language || channel.subject?.language === language))
		.slice(0, 30);

	return (
		<HomeLayout.Widget icon="alarm" color="purple" header={t("needsAttention", "Needs attention")}>
			<LoadingContext
				data={[...needsAttention, ...(discussionChannels || [])]}
				loading={loading || commentsLoading || channelsLoading}
				error={error || commentsError || channelsError}
			>
				<Card fluid>
					<Card.Content style={{ padding: 0 }}>
						{needsAttention.map(({ comment, channel }) => (
							<DiscussionFeedListItem key={comment.id} channel={channel} comment={comment} />
						))}

						{discussionChannels?.slice(0, 5).map((channel) => {
							const lastCommentSender =
								channel.lastComment.sender.ref.path.includes("users") &&
								channel.lastComment.sender.name.split(" ")[0];

							return (
								<DiscussionFeedListItem
									key={channel.id}
									channel={channel}
									lastCommentSender={lastCommentSender}
								/>
							);
						})}
					</Card.Content>
				</Card>
			</LoadingContext>
		</HomeLayout.Widget>
	);
}
